import React, { Suspense } from 'react'
import { AuthRoute, NotFound, Redirect, Route, Switch, useRouteMatch } from '@fs/zion-router'
import ErrorBoundary from '@fs/zion-error-boundary'
import { useUser } from '@fs/zion-user'
import Root from '@fs/zion-root'
import { AppLayout } from '@fs/zion-ui'
import { Navigate } from 'react-router-dom'
import { i18n } from '@fs/zion-locale'
import { NoticeLoading } from '@fs/zion-icon'
import useQualifiesForAccount from './hooks/useQualifiesForAccount'

const LandingPage = React.lazy(() => import('./components/landing/LandingPage'))
const AccessPage = React.lazy(() => import('./components/subscriptions/Access'))
const AncestryPage = React.lazy(() => import('./components/partners/Ancestry'))
const MyHeritagePage = React.lazy(() => import('./components/partners/MyHeritage'))
const FindMyPastPage = React.lazy(() => import('./components/partners/FindMyPast'))
const AmericanAncestorsPage = React.lazy(() => import('./components/partners/AmericanAncestors'))
const MemberSubscriptionsPage = React.lazy(() => import('./components/subscriptions/MemberSubscriptions'))
const Filae = React.lazy(() => import('./components/partners/Filae'))
const Geneanet = React.lazy(() => import('./components/partners/Geneanet'))
const GenealogyBank = React.lazy(() => import('./components/partners/GenealogyBank'))
const Storied = React.lazy(() => import('./components/partners/Storied'))
const MyHeritageHints = React.lazy(() => import('./components/partners/MyHeritageHints'))

const AccessRoute = () => {
  const { path } = useRouteMatch()
  return (
    <Switch>
      <Route exact path={path} access component={AccessPage} requirePersonalData />
      <Route component={NotFound} requirePersonalData />
    </Switch>
  )
}

const MemberSubscriptionsRoute = () => {
  const { path } = useRouteMatch()
  return (
    <Switch>
      <Route exact path={path} member-subscriptions component={MemberSubscriptionsPage} requirePersonalData />
      <Route component={NotFound} requirePersonalData />
    </Switch>
  )
}
const Centers = () => {
  // 👇️ redirect to external URL
  // centers only exists on prod otherwise use - const target = `${baseUrl}/centers`
  window.location.replace(`https://www.familysearch.org/${i18n.language}/centers/about`)
  return null
}
const FlexRoute = ({ componentUnauthenticated, componentAuthenticated, ...props }) => {
  const { signedIn } = useUser()
  const PublicPrivateRoute = signedIn ? AuthorizedForRoute : Route
  return (
    <PublicPrivateRoute
      component={signedIn ? componentAuthenticated : componentUnauthenticated}
      {...props}
      requirePersonalData
    />
  )
}
export default function AppRoutes() {
  return (
    <Root analytics>
      <AppLayout fullWidth>
        <ErrorBoundary>
          <Suspense fallback={<NoticeLoading />}>
            <Switch>
              <AuthenticatedRoute exact path="/" component={AccessRoute} />
              <Route exact path="/centers" component={Centers} requirePersonalData />
              <Route path="/access" element={<Navigate to="/" />} requirePersonalData />
              <Route path="/myheritage/matches" component={MyHeritageHints} />
              <FlexRoute
                path="/ancestry"
                componentUnauthenticated={LandingPage}
                componentAuthenticated={AncestryPage}
              />
              <FlexRoute
                path="/myheritage"
                componentUnauthenticated={LandingPage}
                componentAuthenticated={MyHeritagePage}
              />
              <FlexRoute
                path="/findmypast"
                componentUnauthenticated={LandingPage}
                componentAuthenticated={FindMyPastPage}
              />
              <FlexRoute
                path="/americanancestors"
                componentUnauthenticated={LandingPage}
                componentAuthenticated={AmericanAncestorsPage}
              />
              <FlexRoute path="/filae" componentUnauthenticated={LandingPage} componentAuthenticated={Filae} />
              <FlexRoute path="/geneanet" componentUnauthenticated={LandingPage} componentAuthenticated={Geneanet} />
              <FlexRoute
                path="/genealogybank"
                componentUnauthenticated={LandingPage}
                componentAuthenticated={GenealogyBank}
              />
              <FlexRoute path="/storied" componentUnauthenticated={LandingPage} componentAuthenticated={Storied} />
              <AuthRoute requirePersonalData>
                <AuthorizedForRoute exact path="/member-subscriptions" component={MemberSubscriptionsRoute} />
              </AuthRoute>
              <Route component={NotFound} requirePersonalData />
            </Switch>
          </Suspense>
        </ErrorBoundary>
      </AppLayout>
    </Root>
  )
}

function AuthorizedForRoute({ component: Component, ...rest }) {
  const isAuthorized = useQualifiesForAccount()
  if (isAuthorized === undefined) {
    return <NoticeLoading />
  }
  return (
    <Suspense fallback={<NoticeLoading />}>
      <Route
        {...rest}
        render={(props) => (isAuthorized ? <Component {...props} /> : <Redirect to="/centers" />)}
        requirePersonalData
      />
    </Suspense>
  )
}

function AuthenticatedRoute({ component: Component, ...rest }) {
  const { userLoading, signedIn } = useUser()
  if (userLoading) return null
  return (
    <Route
      {...rest}
      render={(props) => (signedIn ? <Redirect to="/member-subscriptions" /> : <Component {...props} />)}
      requirePersonalData
    />
  )
}
